import { useEffect, useState } from 'react';
import { m } from 'framer-motion';
import { getCategoryMeta, levelTitle, tierFor } from '@brainfart/shared';
import { getProfile, getCategoryLevels, currentStreak } from '../profileStore';
import { useIdentity } from '../identity';
import { ProfileChip } from './ProfileSheet';
import { cachedRatings, fetchRatings, type CategoryRating } from '../ratingsCache';
import { defaultAvatar } from '@brainfart/shared';
import { BackButton } from './BackButton';
import { RequestTopicLink } from './TopicRequest';
import { ACHIEVEMENTS } from '../achievements';

interface StatsScreenProps {
  onHome: () => void;
}

function StatCard({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div style={{ flex: '1 1 120px', background: '#1B1830', border: '1px solid #2E2B45', borderRadius: '14px', padding: '1rem', textAlign: 'center' }}>
      <div style={{ fontSize: '1.6rem', fontWeight: 800, fontFamily: 'Space Grotesk', color: accent || '#fff', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '0.35rem' }}>{label}</div>
    </div>
  );
}

export function StatsScreen({ onHome }: StatsScreenProps) {
  const profile = getProfile();
  const levels = getCategoryLevels();
  const streak = currentStreak();
  const { name, avatar } = useIdentity();
  const [ratings, setRatings] = useState<Record<string, CategoryRating>>(cachedRatings);
  useEffect(() => { fetchRatings().then(setRatings).catch(() => {}); }, []);
  const [showAllTopics, setShowAllTopics] = useState(false);
  const [showAllBadges, setShowAllBadges] = useState(false);

  const duels = Object.values(profile.duels).reduce(
    (acc, d) => ({ wins: acc.wins + d.wins, played: acc.played + d.wins + d.losses + d.ties }), { wins: 0, played: 0 });

  const accuracy = profile.stats.totalAnswered > 0
    ? Math.round((profile.stats.totalCorrect / profile.stats.totalAnswered) * 100)
    : 0;

  const rivalries = Object.entries(profile.rivalries)
    .sort((a, b) => (b[1].wins + b[1].losses + b[1].ties) - (a[1].wins + a[1].losses + a[1].ties))
    .slice(0, 6);

  return (
    <m.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ minHeight: '100vh', background: '#0F0D1F' }}
    >
      <header style={{ padding: 'clamp(0.85rem, 3vw, 1.25rem) 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #2E2B45', position: 'sticky', top: 0, zIndex: 10, background: 'rgba(15,13,31,0.9)', backdropFilter: 'blur(10px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <BackButton onClick={onHome} label="Back to home" />
          <button onClick={onHome} aria-label="BrainFart.fun home" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            <span style={{ fontSize: '1.5rem' }}>🧠</span>
            <h1 style={{ fontSize: '1.2rem', fontWeight: 700, background: 'linear-gradient(135deg, #FFC93C, #FF9F1C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              BrainFart.fun
            </h1>
          </button>
        </div>
      </header>

      <main style={{ maxWidth: 720, margin: '0 auto', padding: '1.5rem', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '3rem', lineHeight: 1, marginBottom: '0.4rem' }}>{avatar}</div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', marginBottom: '0.6rem' }}>
            {name ? `${name}'s Stats` : 'Your Stats'}
          </h2>
          <ProfileChip />
        </div>

        {/* Top-line stats */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '2rem' }}>
          <StatCard label="Day streak" value={`${streak}🔥`} accent="#FFC93C" />
          <StatCard label="Accuracy" value={`${accuracy}%`} accent="#39FF88" />
          <StatCard label="Duels won" value={`${duels.wins}/${duels.played}`} accent="#FF6B6B" />
          <StatCard label="Daily played" value={String(profile.stats.dailyGames)} />
        </div>

        {/* Your topics: level and duel rating in one list */}
        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#ccc', marginBottom: '0.75rem' }}>🏅 Your topics</h3>
        {levels.length === 0 ? (
          <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '2rem' }}>
            Play a quiz to start levelling up your topics.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '2rem' }}>
            {levels.slice(0, showAllTopics ? 12 : 4).map(lvl => {
              const meta = getCategoryMeta(lvl.key);
              const color = meta?.color || '#FFC93C';
              const pct = Math.round(lvl.progress * 100);
              const rating = ratings[lvl.key]?.rating;
              return (
                <div key={lvl.key} style={{ background: '#1B1830', border: `1px solid ${color}33`, borderRadius: '12px', padding: '0.85rem 1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1.2rem' }}>{meta?.icon || '🎯'}</span>
                    <span style={{ fontWeight: 700, color, flex: 1 }}>{meta?.title || lvl.key}</span>
                    <span style={{ fontSize: '0.75rem', color: '#888', textAlign: 'right' }}>
                      {rating !== undefined && <strong style={{ color: tierFor(rating).color, marginRight: '0.5rem' }}>{tierFor(rating).icon} {rating.toLocaleString()}</strong>}
                      <strong style={{ color: '#FFC93C' }}>{levelTitle(lvl.level)}</strong> · Lv {lvl.level}
                    </span>
                  </div>
                  <div style={{ height: '6px', borderRadius: '100px', background: '#2E2B45', overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: color, transition: 'width 0.4s' }} />
                  </div>
                </div>
              );
            })}
            {levels.length > 4 && (
              <button className="text-link" onClick={() => setShowAllTopics(v => !v)} style={{ alignSelf: 'center' }}>
                {showAllTopics ? 'Show fewer' : `Show all ${Math.min(levels.length, 12)} topics`}
              </button>
            )}
          </div>
        )}

        {/* Achievements: the ones you have; the rest are one tap away */}
        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#ccc', marginBottom: '0.75rem' }}>
          🏅 Achievements · {Object.keys(profile.achievements).length}/{ACHIEVEMENTS.length}
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {ACHIEVEMENTS.filter(a => showAllBadges || profile.achievements[a.id]).map(a => {
            const got = Boolean(profile.achievements[a.id]);
            return (
              <div key={a.id} title={a.desc} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '0.2rem', padding: '0.7rem 0.4rem',
                borderRadius: '12px', background: got ? 'rgba(255,201,60,0.1)' : '#141226', border: `1px solid ${got ? 'rgba(255,201,60,0.4)' : '#2E2B45'}`,
              }}>
                <span style={{ fontSize: '1.7rem', lineHeight: 1, filter: got ? 'none' : 'grayscale(1)', opacity: got ? 1 : 0.35 }}>{a.icon}</span>
                <span style={{ fontSize: '0.75rem', fontWeight: 800, color: got ? '#fff' : '#777' }}>{a.title}</span>
                <span style={{ fontSize: '0.65rem', color: '#777', lineHeight: 1.3 }}>{a.desc}</span>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
          <button className="text-link" onClick={() => setShowAllBadges(v => !v)}>
            {showAllBadges ? 'Hide locked' : `Show all ${ACHIEVEMENTS.length} achievements`}
          </button>
        </div>

        {/* Rivalries */}
        {rivalries.length > 0 && (
          <>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#ccc', marginBottom: '0.75rem' }}>⚔️ Rivalries</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {rivalries.map(([opponent, rec]) => (
                <div key={opponent} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1B1830', border: '1px solid #2E2B45', borderRadius: '12px', padding: '0.75rem 1rem' }}>
                  <span style={{ fontWeight: 600, color: '#fff' }}>{rec.avatar ?? defaultAvatar(opponent)} vs {opponent}</span>
                  <span style={{ fontSize: '0.85rem', color: '#888' }}>
                    <span style={{ color: '#39FF88', fontWeight: 700 }}>{rec.wins}W</span>
                    {' · '}
                    <span style={{ color: '#FF4D6A', fontWeight: 700 }}>{rec.losses}L</span>
                    {rec.ties > 0 && <> · {rec.ties}T</>}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
          <RequestTopicLink where="stats" />
        </div>
      </main>
    </m.div>
  );
}
